import { cn } from "@/lib/utils";
import { TrendIndicator } from "./TrendIndicator";

/**
 * LOCKED, product-approved wording for the current 4 KPI metrics (from
 * analyticsService's `OfferingTypeMetrics`). This table is reference material for
 * whichever later task wires up the real Insights page — copy it VERBATIM, do not
 * rephrase or re-derive the wording.
 *
 * | Metric (backend field)  | Label                      | Subcaption                     |
 * |--------------------------|-----------------------------|----------------------------------|
 * | totalImpressions         | Times students saw you      | in search results                |
 * | clickThroughRate         | Turned into profile views   | out of everyone who saw you      |
 * | totalInquiries           | Sent you an inquiry         | students who reached out         |
 * | avgResponseTimeMinutes   | Your typical reply time     | from their first message         |
 *
 * `KpiTile` itself is generic — it does not hardcode any of these 4 rows, it just
 * accepts `label`/`subcaption` as props.
 */

export interface KpiTileProps {
  label: string;
  subcaption: string;
  value: string | number;
  trend?: { current: number; prior: number | null };
  cohortMedian?: string;
  /** When true, renders without card border/shadow/bg — for use inside an already-bordered card. */
  embedded?: boolean;
  className?: string;
}

/**
 * Single consistent KPI tile treatment — replaces the old `StatCard`'s six-color
 * pastel cycling ("decorative variety rather than meaning"). Colour is reserved only
 * for the trend indicator's semantic meaning, never for tile decoration.
 */
export function KpiTile({ label, subcaption, value, trend, cohortMedian, embedded = false, className }: KpiTileProps) {
  return (
    <div className={cn(
      embedded
        ? "rounded-xl bg-white/80 p-4"
        : "rounded-2xl border border-border bg-card shadow-sm p-5",
      className
    )}>
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{subcaption}</p>
      <p className="font-display text-3xl font-bold text-foreground mt-3 tracking-tight">{value}</p>
      {(trend || cohortMedian) && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {trend && <TrendIndicator current={trend.current} prior={trend.prior} />}
          {cohortMedian && <span className="text-xs text-muted-foreground">{cohortMedian}</span>}
        </div>
      )}
    </div>
  );
}

export default KpiTile;
